import { useState, useCallback, useEffect, useRef } from "react";
import {
  Container, Row, Col, Card, CardBody, Badge, Button, Input,
  Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Alert,
  Nav, NavItem, NavLink
} from "reactstrap";
import UserProfileService from "../../../services/UserProfileService";
import { useQuery } from "@tanstack/react-query";

// ─── Theme Tokens ────────────────────────────────────────────────────────────
const T = {
  navy: "#0f1e3c",
  ink: "#1a2b4a",
  blue: "#1e5aff",
  blueSoft: "#e8eeff",
  teal: "#00b4aa",
  amber: "#f59e0b",
  green: "#16a34a",
  gray: "#8a94a6",
  grayLt: "#f4f6fa",
  white: "#ffffff",
  border: "#e2e8f0",
  shadow: "0 2px 16px rgba(15,30,60,0.08)",
  shadowHover: "0 6px 28px rgba(15,30,60,0.14)",
  radius: "14px",
  radiusSm: "8px",
};

const font = {
  display: "'Playfair Display', Georgia, serif",
  body: "'DM Sans', 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', monospace",
};

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400&display=swap');

  * { box-sizing: border-box; }

  body { font-family: ${font.body}; background: #eef1f7; margin: 0; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse-ring {
    0%   { box-shadow: 0 0 0 0 rgba(30,90,255,0.35); }
    70%  { box-shadow: 0 0 0 10px rgba(30,90,255,0); }
    100% { box-shadow: 0 0 0 0 rgba(30,90,255,0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }

  .profile-card   { animation: fadeUp 0.5s ease both; }
  .portal-row     { animation: fadeUp 0.5s ease both; }
  .portal-row:nth-child(1) { animation-delay: 0.05s; }
  .portal-row:nth-child(2) { animation-delay: 0.10s; }
  .portal-row:nth-child(3) { animation-delay: 0.15s; }
  .portal-row:nth-child(4) { animation-delay: 0.20s; }

  .toggle-switch input { display: none; }
  .toggle-switch label {
    display: inline-block; width: 44px; height: 24px;
    background: #cbd5e1; border-radius: 12px; cursor: pointer;
    position: relative; transition: background 0.25s;
  }
  .toggle-switch label::after {
    content: ''; position: absolute; top: 3px; left: 3px;
    width: 18px; height: 18px; border-radius: 50%;
    background: white; transition: transform 0.25s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }
  .toggle-switch input:checked + label { background: ${T.blue}; }
  .toggle-switch input:checked + label::after { transform: translateX(20px); }

  .action-btn {
    background: ${T.blue}; color: white; border: none;
    padding: 7px 18px; border-radius: 8px; font-size: 13px;
    font-family: ${font.body}; font-weight: 500;
    cursor: pointer; transition: all 0.2s; white-space: nowrap;
  }
  .action-btn:hover:not(:disabled) {
    background: #1648d4; transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(30,90,255,0.35);
  }
  .action-btn:disabled {
    background: #cbd5e1; cursor: not-allowed; transform: none;
  }
  .action-btn.loading { animation: pulse-ring 1.4s ease infinite; }

  .portal-expand {
    max-height: 0; overflow: hidden; transition: max-height 0.3s ease, opacity 0.3s ease;
    opacity: 0;
  }
  .portal-expand.open { max-height: 200px; opacity: 1; }

  .avatar-ring { animation: pulse-ring 2.5s ease infinite; }

  .info-label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.08em; color: ${T.gray}; margin-bottom: 2px;
  }
  .info-value { font-size: 14.5px; font-weight: 500; color: ${T.ink}; }

  .tab-btn {
    background: none; border: none; padding: 8px 20px;
    font-family: ${font.body}; font-size: 14px; font-weight: 500;
    color: ${T.gray}; cursor: pointer; border-radius: 8px;
    transition: all 0.2s; position: relative;
    white-space: nowrap;
  }
  .tab-btn.active {
    color: ${T.blue}; background: ${T.blueSoft};
  }
  .tab-btn:hover:not(.active) { background: ${T.grayLt}; color: ${T.ink}; }

  /* Responsive Utilities */
  .profile-layout {
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 24px;
    align-items: start;
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 24px;
  }

  .tabs-container {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }
  
  .page-container {
    min-height: calc(100vh - 56px);
    background: #eef1f7;
    padding: 32px 24px;
    font-family: ${font.body};
  }

  .portal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 10px;
  }

  @media (max-width: 900px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .profile-layout {
      grid-template-columns: 1fr;
    }
    .profile-layout > div:first-child {
      margin-inline: auto;
      width: 100%;
      max-width: 480px;
    }
    .portal-row {
      flex-direction: column;
      align-items: flex-start;
    }
    .portal-row > div > div {
      margin-bottom: 4px;
    }
  }

  @media (max-width: 480px) {
    .stats-grid {
      grid-template-columns: 1fr;
    }
    .page-container {
      padding: 16px 12px;
    }
    .portal-expand.open { max-height: 300px; }
  }
\`;sor: pointer; border-radius: 8px;
    transition: all 0.2s; position: relative;
  }
  .tab-btn.active {
    color: ${T.blue}; background: ${T.blueSoft};
  }
  .tab-btn:hover:not(.active) { background: ${T.grayLt}; color: ${T.ink}; }
`;

// ─── Status badge config ──────────────────────────────────────────────────────
const STATUS = {
  "Not Applied": { color: T.gray, bg: "#f1f3f7", label: "Not Applied" },
  "Pending": { color: "#b45309", bg: "#fef9c3", label: "Pending" },
  "Approved": { color: "#15803d", bg: "#dcfce7", label: "Approved" },
  "Rejected": { color: "#dc2626", bg: "#fee2e2", label: "Rejected" },
};

const userData = localStorage.getItem("user");
const user = JSON.parse(userData);




function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS["Not Applied"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color,
      padding: "4px 12px", borderRadius: 20,
      fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: s.color, display: "inline-block",
      }} />
      {s.label}
    </span>
  );
}

function useContainerWidth(ref) {
  const [width, setWidth] = useState(400);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

// ─── UserInfoCard ─────────────────────────────────────────────────────────────
function UserInfoCard({ user }) {
  const ref = useRef(null);
  const width = useContainerWidth(ref);
  const compact = width < 380; // single-column below this width


  const initials = user.fullName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <>
      {/* Inject Google Font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .uic-root * { box-sizing: border-box; }
 
        /* Info label / value */
        .uic-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: .06em;
          color: ${T.gray};
          margin-bottom: 3px;
          font-family: ${font.body};
        }
        .uic-value {
          font-size: 14px;
          font-weight: 500;
          color: ${T.ink};
          font-family: ${font.body};
          /* ← THE FIX: prevent overflow / truncation */
          word-break: break-word;
          overflow-wrap: anywhere;
          hyphens: auto;
          line-height: 1.45;
        }
 
        /* Info grid */
        .uic-grid {
          display: grid;
          gap: 16px 20px;
        }
        .uic-grid-2col { grid-template-columns: 1fr 1fr; }
        .uic-grid-1col { grid-template-columns: 1fr; }
        .uic-full { grid-column: 1 / -1; }
      `}</style>

      <div
        ref={ref}
        className="uic-root"
        style={{
          borderRadius: T.radius,
          boxShadow: T.shadow,
          overflow: "hidden",
          background: "#fff",
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          fontFamily: font.body,
        }}
      >
        {/* ── Body ── */}
        <div style={{ padding: "clamp(16px, 5%, 28px)" }}>
          {/* Avatar row */}
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            {/* Initials avatar */}
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${T.blue}, ${T.teal})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "3px solid white",
                boxShadow: T.shadow,
                fontFamily: font.display,
                fontSize: 26,
                fontWeight: 700,
                color: "white",
                flexShrink: 0,
                userSelect: "none",
              }}
            >
              {initials}
            </div>

            {/* Status badge */}
            <span
              style={{
                background: "#dcfce7",
                color: "#15803d",
                padding: "4px 12px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 4,
                whiteSpace: "nowrap",
              }}
            >
              ● Active
            </span>
          </div>

          {/* Name & role */}
          <div style={{ marginBottom: 20 }}>
            <h2
              style={{
                fontFamily: font.display,
                fontSize: "clamp(18px, 4vw, 22px)",
                fontWeight: 700,
                color: T.navy,
                margin: 0,
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {user.fullName}
            </h2>
            <p
              style={{
                color: T.gray,
                fontSize: 13,
                margin: "4px 0 0",
                fontWeight: 500,
                wordBreak: "break-word",
              }}
            >
              {user.roles?.[0]}
            </p>
          </div>

          {/* Divider */}
          <hr
            style={{
              border: "none",
              borderTop: `1px solid ${T.border}`,
              margin: "0 0 20px",
            }}
          />

          {/* Info grid — 2-col on wide, 1-col on narrow */}
          <div
            className={`uic-grid ${compact ? "uic-grid-1col" : "uic-grid-2col"}`}
          >
            {[
              { label: "Email", value: user.email },
              { label: "Phone", value: user.phone },
            ].map(({ label, value, full }) => (
              <div
                key={label}
                className={full || compact ? "uic-full" : ""}
              >
                <div className="uic-label">{label}</div>
                <div className="uic-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── PortalRow ────────────────────────────────────────────────────────────────
function PortalRow({ portal, onApply }) {
  const [expanded, setExpanded] = useState(false);
  const [notify, setNotify] = useState(portal.notify);
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(portal.status);

  useEffect(() => {
    setStatus(portal.status);
  }, [portal.status]);

  const canApply = status === "Not Applied" || status === "Rejected";

  const handleApply = async () => {
    if (!remark.trim()) { alert("Please enter a remark/justification."); return; }
    setLoading(true);
    await UserProfileService.applyForPortal({ portal: parseInt(portal.enum, 10), remark });
    setStatus("Pending");
    setLoading(false);
    setExpanded(false);
    onApply?.(portal.id, { notify, remark });
  };

  return (
    <div className="portal-row" style={{
      background: T.white, borderRadius: T.radiusSm,
      border: `1px solid ${T.border}`,
      overflow: "hidden", transition: "box-shadow 0.2s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = T.shadowHover}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* Main row */}
      <div style={{
        display: "flex", alignItems: "center",
        padding: "14px 20px", gap: 12, flexWrap: "wrap",
      }}>
        {/* Icon */}
        {/* <div style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: T.blueSoft, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 18,
        }}>
          {portal.icon}
        </div> */}

        {/* Name + description */}
        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: T.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {portal.name}
          </div>
          <div style={{ fontSize: 12, color: T.gray, marginTop: 1 }}>{portal.description}</div>
        </div>

        {/* Notification toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: T.gray, fontWeight: 500 }}>Notify</span>
          <div className="toggle-switch">
            <input
              type="checkbox" id={`notif-${portal.id}`}
              checked={notify} onChange={e => setNotify(e.target.checked)}
            />
            <label htmlFor={`notif-${portal.id}`} />
          </div>
        </div>

        {/* Status */}
        <div style={{ flexShrink: 0 }}>
          <StatusBadge status={status} />
        </div>

        {/* Action */}
        <div style={{ flexShrink: 0, display: "flex", gap: 8 }}>
          {canApply && (
            <button
              className={`action-btn${loading ? " loading" : ""}`}
              onClick={() => setExpanded(v => !v)}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Spinner size="sm" /> Applying…
                </span>
              ) : expanded ? "Cancel" : "Request Access"}
            </button>
          )}
          {status === "Approved" && (
            <button className="action-btn" style={{ background: T.teal }}
              onClick={() => window.open(portal.url, "_blank")}>
              Open Portal →
            </button>
          )}
          {status === "Pending" && (
            <button className="action-btn" style={{ background: T.amber, cursor: "default" }} disabled>
              Awaiting Review
            </button>
          )}
        </div>
      </div>

      {/* Expandable remarks section */}
      <div className={`portal-expand${expanded ? " open" : ""}`}>
        <div style={{
          padding: "0 20px 16px",
          borderTop: `1px dashed ${T.border}`,
          paddingTop: 14,
          background: T.grayLt,
        }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: T.gray, display: "block", marginBottom: 6 }}>
            Justification / Remarks <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <Input
            type="textarea" rows={2} placeholder="Briefly explain why you need access to this portal…"
            value={remark} onChange={e => setRemark(e.target.value)}
            style={{
              fontSize: 13, borderColor: T.border, borderRadius: 8,
              fontFamily: font.body, resize: "none",
              background: T.white,
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
            <button className="action-btn" style={{ background: T.grayLt, color: T.ink, border: `1px solid ${T.border}` }}
              onClick={() => setExpanded(false)}>
              Cancel
            </button>
            <button className={`action-btn${loading ? " loading" : ""}`}
              onClick={handleApply} disabled={loading || !remark.trim()}>
              {loading ? <><Spinner size="sm" /> &nbsp;Submitting</> : "Submit Request"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ActivationSection ────────────────────────────────────────────────────────
function ActivationSection({ portals, onApply }) {
  const [activeTab, setActiveTab] = useState("all");
  const [toast, setToast] = useState(null);

  const tabs = [
    { key: "all", label: "All Portals" },
    { key: "Not Applied", label: "Not Applied" },
    { key: "Pending", label: "Pending" },
    { key: "Approved", label: "Approved" },
  ];

  const filtered = activeTab === "all"
    ? portals
    : portals.filter(p => p.status === activeTab);

  const handleApply = (id, data) => {
    setToast({ type: "success", msg: "Access request submitted successfully!" });
    setTimeout(() => setToast(null), 3500);
    onApply?.(id, data);
  };

  return (
    <Card style={{ border: "none", borderRadius: T.radius, boxShadow: T.shadow }}>
      {/* Header */}
      <div style={{
        padding: "22px 28px 0",
        borderBottom: `1px solid ${T.border}`,
      }}>
        <div className="portal-header">
          <div>
            <h3 style={{
              fontFamily: font.display, fontSize: 18, fontWeight: 700,
              color: T.navy, margin: 0,
            }}>Portal Access Management</h3>
            <p style={{ fontSize: 13, color: T.gray, margin: "4px 0 0" }}>
              Request and manage your portal access permissions
            </p>
          </div>
          <div style={{
            background: T.blueSoft, color: T.blue,
            padding: "6px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600,
          }}>
            {portals.filter(p => p.status === "Approved").length} / {portals.length} Active
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          {tabs.map(t => (
            <button key={t.key} className={`tab-btn${activeTab === t.key ? " active" : ""}`}
              onClick={() => setActiveTab(t.key)}>
              {t.label}
              <span style={{
                marginLeft: 6, fontSize: 11,
                background: activeTab === t.key ? T.blue : "#e2e8f0",
                color: activeTab === t.key ? "white" : T.gray,
                padding: "1px 7px", borderRadius: 10, fontWeight: 600,
              }}>
                {t.key === "all" ? portals.length : portals.filter(p => p.status === t.key).length}
              </span>
            </button>
          ))}
        </div>
      </div>

      <CardBody style={{ padding: "20px 28px 28px" }}>
        {/* Toast */}
        {toast && (
          <div style={{
            background: toast.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${toast.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            color: toast.type === "success" ? "#166534" : "#991b1b",
            padding: "12px 16px", borderRadius: 8, marginBottom: 16,
            fontSize: 13, fontWeight: 500, display: "flex", gap: 8, alignItems: "center",
            animation: "fadeUp 0.3s ease",
          }}>
            {toast.type === "success" ? "✓" : "✕"} {toast.msg}
          </div>
        )}

        {/* Portal list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.length === 0 ? (
            <div style={{
              textAlign: "center", padding: "40px 0",
              color: T.gray, fontSize: 14,
            }}>
              No portals in this category.
            </div>
          ) : (
            filtered.map(p => (
              <PortalRow key={p.id} portal={p} onApply={handleApply} />
            ))
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ─── Stats strip ─────────────────────────────────────────────────────────────
function StatsStrip({ portals }) {
  const stats = [
    { label: "Total Portals", value: portals.length, color: T.blue },
    { label: "Approved", value: portals.filter(p => p.status === "Approved").length, color: T.teal },
    { label: "Pending", value: portals.filter(p => p.status === "Pending").length, color: T.amber },
    { label: "Not Applied", value: portals.filter(p => p.status === "Not Applied").length, color: T.gray },
  ];

  return (
    <div className="stats-grid">
      {stats.map(s => (
        <div key={s.label} className="profile-card" style={{
          background: T.white, borderRadius: T.radiusSm,
          boxShadow: T.shadow, padding: "16px 20px",
          borderLeft: `3px solid ${s.color}`,
        }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: s.color, fontFamily: font.mono }}>
            {s.value}
          </div>
          <div style={{ fontSize: 12, color: T.gray, fontWeight: 500, marginTop: 2 }}>
            {s.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────
const USER = {
  name: "Ahmad Faruq Razali",
  username: "ahmad.faruq",
  email: "ahmad.faruq@university.edu.my",
  phone: "+60 12-345 6789",
  role: "Senior Lecturer",
  department: "Faculty of Computing",
  empId: "EMP-2024-0047",
  address: "Block C, Level 3, University Technology Campus, 43000 Kajang, Selangor",
};

const PORTALS_INIT = [
  {
    id: 1,
    name: "Smart Kampus",
    description: "Campus management & academic resource portal",
    status: "Approved",
    enum: "3",
    notify: true,
    url: "https://smartkampus.kangarooedu.com/"
  },
  {
    id: 2,
    name: "Offshore",
    description: "Offshore intelligence & analytics dashboard",
    status: "Pending",
    enum: "2",
    notify: false,
    url: "http://backend.kangarooedu.com/"
  },
  {
    id: 3,
    name: "Onshore",
    description: "Onshore intelligence & analytics dashboard",
    status: "Not Applied",
    enum: "1",
    notify: false,
    url: "http://backend.kangarooedu.com/"
  },
];

export default function UserProfile() {
  const { data: applications, isLoading } = useQuery({
    queryKey: ["applications"],
    queryFn: () => UserProfileService.getApplications(),
  });

  const [portals, setPortals] = useState(PORTALS_INIT);

  useEffect(() => {
    if (applications) {
      setPortals(PORTALS_INIT.map(p => {
        const found = applications.find(a =>
          a.portal.replace(/\s+/g, '').toLowerCase() === p.name.replace(/\s+/g, '').toLowerCase()
        );
        return {
          ...p,
          status: found ? found.status : (p.name === "Smart Kampus" ? p.status : "Not Applied")
        };
      }));
    }
  }, [applications]);

  const handleApply = useCallback((id) => {
    setPortals(prev => prev.map(p =>
      p.id === id ? { ...p, status: "Pending" } : p
    ));
  }, []);

  return (
    <>
      <style>{STYLE}</style>

      {/* Top nav bar */}
      {/* <div style={{
        background: T.navy, padding: "0 32px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
        fontFamily: font.body,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>🎓</span>
          <span style={{
            fontFamily: font.display, color: "white", fontSize: 17, fontWeight: 600,
          }}>UniPortal</span>
          <span style={{
            background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)",
            padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500,
            marginLeft: 4,
          }}>v2.4</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
            {new Date().toLocaleDateString("en-MY", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </span>
          <div style={{
            width: 34, height: 34, borderRadius: "50%",
            background: `linear-gradient(135deg, ${T.blue}, ${T.teal})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>AF</div>
        </div>
      </div> */}

      {/* Page body */}
      <div className="page-container">
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>

          {/* Page title */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{
              fontFamily: font.display, fontSize: 28, fontWeight: 700,
              color: T.navy, margin: 0,
            }}>My Profile</h1>
            <p style={{ color: T.gray, margin: "6px 0 0", fontSize: 14 }}>
              Manage your personal information and portal access
            </p>
          </div>

          {/* Stats */}
          {/* <StatsStrip portals={portals} /> */}

          {/* Two column layout */}
          <div className="profile-layout">
            {/* Left: user card */}
            <div>
              <UserInfoCard user={user} />

              {/* Quick links */}
              {/* <Card style={{
                border: "none", borderRadius: T.radius, boxShadow: T.shadow,
                marginTop: 18,
              }}>
                <CardBody style={{ padding: 20 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.08em", color: T.gray, marginBottom: 12,
                  }}>Quick Actions</div>
                  {[
                    { icon: "✏️", label: "Edit Profile" },
                    { icon: "🔒", label: "Change Password" },
                    { icon: "🔔", label: "Notification Prefs" },
                    { icon: "📥", label: "Download Profile" },
                  ].map(a => (
                    <div key={a.label} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "9px 12px", borderRadius: 8, cursor: "pointer",
                      transition: "background 0.15s", fontSize: 13, fontWeight: 500,
                      color: T.ink,
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = T.grayLt}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span>{a.icon}</span> {a.label}
                    </div>
                  ))}
                </CardBody>
              </Card> */}
            </div>

            {/* Right: activation section */}
            <ActivationSection portals={portals} onApply={handleApply} />
          </div>

          {/* Footer */}

        </div>
      </div>
    </>
  );
}